import { generateRequest } from './request.js';
import { AveRequestEvents } from '../events.js';
/**
 * Load the changes to the specified car & configuration with a certain transition with the listed animations.
 *
 * Limitations:
 * - Not available while an environment is loading.
 * - Not available while another car is loading.
 * - Not available while an experience is running.
 * - Not available while a driving scene is running.
 * - Not available while a image sequence is rendering.
 *
 * Notes:
 * - Aborts all currently running animations.
 * - If no engine code and model year are provided, they are read from the PR String.
 *   Therefore, the PR String has to be formatted in a specific way.
 *   Engine code has to be the second code and the model year the third.
 *   If this is not the case, the car will not load correctly or at all.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "lastUpdate": true,
 *     "name": "AVESelectCar",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "PrStringList": [
 *       "51272",
 *       "F4BAC3",
 *       "2022",
 *       <...>
 *     ],
 *     "Animations": [
 *       {
 *         "AnimationIds": [
 *           "Doors"
 *         ],
 *         "Direction": "forward",
 *         "Skip": true
 *       }
 *     ],
 *     "ResetCar": false,
 *     "LoadingScreen": "Auto",
 *     "ConsumptionData": [
 *       "test"
 *     ],
 *     "PreserveCarAnimationStates": false,
 *     "ModelRevision": "MP2"
 *   }
 * }]
 * ```
 */
export function generateSelectCarRequest({ prStringList, engineCode, modelYear, animations, resetCar, loadingScreen, consumptionData, preserveCarAnimationStates, modelRevision, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVESelectCar,
        data: {
            PrStringList: prStringList,
            EngineCode: engineCode,
            ModelYear: modelYear,
            Animations: animations,
            ResetCar: resetCar,
            LoadingScreen: loadingScreen,
            ConsumptionData: consumptionData,
            PreserveCarAnimationStates: preserveCarAnimationStates,
            ModelRevision: modelRevision
        }
    });
}
/**
 * Set and reset car visibility and also reset car parts hidden or isolated before.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVESetCarVisibility",
 *     "type": "REQUEST",
 *     "lastUpdate": true
 *   },
 *   "data": {
 *     "carId": 0,
 *     "visible": true
 *   }
 * }]
 * ```
 */
export function generateSetCarVisibilityRequest({ carId, visible, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVESetCarVisibility,
        data: {
            carId: carId,
            visible
        }
    });
}
/**
 * Only show the car parts having one of the provided Tags.
 * Also reset previous isolated or hidden car parts.
 *
 * Limitations:
 * - Will not inform client if a tag wasn't found.
 *
 * Notes:
 * - Tags are also known as Alias.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEIsolateCarParts",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "tags": ["Wheel", "Brake"]
 *   }
 * }]
 * ```
 */
export function generateIsolateCarPartsRequest({ tags, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVEIsolateCarParts,
        data: { tags }
    });
}
/**
 * Set an absolute steering angle for the wheels.
 *
 * Limitations:
 * - Value will be set immediately and is not animated.
 *
 * Notes:
 * - To reset the angle, this command has to be send with a value of `0`.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVESetSteeringAngle",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "degrees": 25.0
 *   }
 * }]
 * ```
 */
export function generateSetSteeringAngleRequest({ degrees, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVESetSteeringAngle,
        data: { degrees }
    });
}
/**
 * Return all currently visible interaction spots.
 *
 * Notes:
 * - The visibility of an animation spot is not being affected by occlusion
 * - Payload gives an ID, a type and a normalized screen space location ([0.0-1.0], 0,0 is top left, x is right, y is down)
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEGetInteractionSpots",
 *     "type": "REQUEST"
 *   },
 *   "data": {}
 * }]
 * ```
 */
export function generateGetAnimationSpotsRequest({ messageId } = {}) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEGetAnimationSpots });
}
/**
 * Enable or disable regular UPDATE messages with all visible animation spots.
 *
 * Notes:
 * - AVEEndSession stops all animation spots updates
 * - The visibility of an animation spot is not influenced by being occluded
 * - Payload gives an ID, a type and a normalized screen space location ([0.0-1.0], 0,0 is top left, x is right, y is down)
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEReceiveAnimationSpotsUpdate",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "enabled": false
 *   }
 * }]
 * ```
 */
export function generateReceiveAnimationSpotsUpdateRequest({ enabled, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVEReceiveAnimationSpotsUpdate,
        data: { enabled }
    });
}
/**
 * Set the visibility of the animation spots
 *
 * Notes:
 * - The visibility of an animation spot is not influenced by being occluded
 * - Only the corresponding spots for the currently loaded car models available animations are displayed
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVESetAnimationSpotsVisibility",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "enabled": false
 *   }
 * }]
 * ```
 */
export function generateSetAnimationSpotsVisibilityRequest({ enabled, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVESetAnimationSpotsVisibility,
        data: { enabled }
    });
}
/**
 * Return all currently visible interaction spots.
 *
 * Notes:
 * - A visible interaction spot is within rendered area and not occluded.
 * - Payload gives an ID, a type, and a normalized screen space location ([0.0-1.0], 0.0 is top left, x is right, y is down).
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEGetInteractionSpots",
 *     "type": "REQUEST"
 *   },
 *   "data": {}
 * }]
 * ```
 */
export function generateGetInteractionSpotsRequest({ messageId } = {}) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEGetInteractionSpots });
}
/**
 * Enable or disable regular UPDATE messages with all visible interaction spots.
 *
 * Notes:
 * - AVEEndSession stops all interaction spots updates.
 *   A visible interaction spot is within rendered area and not occluded.
 * - Payload gives an ID, a type and a normalized screen space location ([0.0-1.0], 0.0 is top left;, x is right, y is down).
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVEReceiveInteractionSpotsUpdate",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "enabled": false
 *   }
 * }]
 * ```
 */
export function generateReceiveInteractionSpotsUpdateRequest({ enabled, messageId }) {
    return generateRequest({
        messageId,
        name: AveRequestEvents.AVEReceiveInteractionSpotsUpdate,
        data: { enabled }
    });
}
/**
 * Transform the cars location and rotation to the input specifications.
 *
 * Limitations:
 * - Not available while an Environment is loading.
 * - Not available when another car is loading.
 *
 * Notes:
 * - Loading a new car will reset the location and rotation.
 * - Loading a different environment will reset the location and rotation.
 * - Sending the command with an empty payload will reset the location and rotation of the currently loaded car.
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *     "name": "AVESetCarTransform",
 *     "type": "REQUEST"
 *   },
 *   "data": {
 *     "location": {
 *       "x": 5,
 *       "y": 80,
 *       "z": 0
 *     },
 *     "rotation": {
 *       "roll": 15,
 *       "pitch": 15,
 *       "yaw": 2
 *     }
 *   }
 * }]
 * ```
 */
export function generateSetCarTransformRequest({ location, rotation, messageId }) {
    return generateRequest({ messageId, name: AveRequestEvents.AVESetCarTransform, data: { location, rotation } });
}
/**
 * This command returns a list of all available model configurations
 *
 * Limitations:
 * - Not available while an Environment is loading
 * - Not available when car is loading
 *
 * Notes:
 * - Returns the model configurations saved in the AVEVehicleDefinition asset file
 *
 * Example:
 * ```
 * [{
 *   "meta": {
 *      "name": "AVEListModelConfigurations",
 *      "type": "REQUEST",
 *   },
 *   "data": {}
 * }]
 * ```
 */
export function generateListModelConfigurationsRequest({ messageId } = {}) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEListModelConfigurations });
}
export function generateDisplayConsumptionDataRequest({ enabled, messageId }) {
    return generateRequest({ messageId, name: AveRequestEvents.AVEDisplayConsumptionData, data: { enabled } });
}
//# sourceMappingURL=car.js.map